import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";
import useMediaQuery from "./useMediaQuery";
import { Checkbox } from "../ui/checkbox";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

function EmptyState({ height = 140, children = "No data found" }) {
  return (
    <div
      style={{
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px dashed rgba(255,255,255,0.12)",
        borderRadius: 14,
        color: "#c3c8cc",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

export default function DataTable({
  columns,
  rows,
  nestedColumns,
  nestedRowKey,
  nestedTableHeader,
  showCustomNestedComponent = false,
  customNestedComponent,
  totalRows = 0,
  currentPage = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  minWidth = 480,
  isSelectable = false,
  selectableCondition = null,
  onSelectionChange = () => {},
  resetSelection = false,
  getRowCheckboxDisabled,
  selectionRows,
  getRowStyle,
  mobileCompact = false,
}) {
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const expandable = Boolean(showCustomNestedComponent) || Boolean(nestedColumns && nestedColumns.length);

  useEffect(() => {
    if (resetSelection) {
      setSelectedRows([]);
      setSelectedRowIds([]);
      onSelectionChange([]);
    }
  }, [onSelectionChange, resetSelection]);

  const areArraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    const ids1 = new Set(arr1.map((item) => item.id));
    return arr2.every((item) => ids1.has(item.id));
  };

  useEffect(() => {
    if (!isSelectable) return;

    const sourceRows = selectionRows || rows;
    if (sourceRows && sourceRows.length > 0) {
      const newSelectedRows = sourceRows.filter((row) => selectedRowIds.includes(row.id));
      if (!areArraysEqual(newSelectedRows, selectedRows)) {
        setSelectedRows(newSelectedRows);
        onSelectionChange(newSelectedRows);
      }
    } else if (selectedRows.length > 0) {
      setSelectedRows([]);
      onSelectionChange([]);
    }
  }, [isSelectable, onSelectionChange, rows, selectedRowIds, selectedRows, selectionRows]);

  const isRowSelectable = (row) => {
    if (!selectableCondition) return true;
    const [field, operator, value] = selectableCondition;
    switch (operator) {
      case "===":
        return row[field] === value;
      case "!==":
        return row[field] !== value;
      case "includes":
        return row[field]?.includes(value);
      case ">":
        return row[field] > value;
      case "<":
        return row[field] < value;
      case ">=":
        return row[field] >= value;
      case "<=":
        return row[field] <= value;
      default:
        return false;
    }
  };

  const allSelectableRows = (selectionRows || rows || []).filter(isRowSelectable);
  const selectableRows = allSelectableRows.filter(
    (row) => !(getRowCheckboxDisabled && getRowCheckboxDisabled(row))
  );
  const areAllRowsGloballySelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedRows.some((selectedRow) => selectedRow.id === row.id));

  const handleSelectRow = useCallback((event, row) => {
    event.stopPropagation();
    setSelectedRowIds((prev) => {
      const isSelected = prev.includes(row.id);
      return isSelected ? prev.filter((id) => id !== row.id) : [...prev, row.id];
    });
  }, []);

  const handleSelectAllRows = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelectedIds = [
          ...selectedRowIds.filter((id) => !selectableRows.some((row) => row.id === id)),
          ...selectableRows.map((row) => row.id),
        ];
        setSelectedRowIds(newSelectedIds);
      } else {
        const nonSelectableIds = selectedRowIds.filter(
          (id) => !selectableRows.some((row) => row.id === id)
        );
        setSelectedRowIds(nonSelectableIds);
      }
    },
    [selectableRows, selectedRowIds]
  );

  const isRowSelected = useCallback((row) => selectedRowIds.includes(row.id), [selectedRowIds]);

  const renderCheckbox = ({ checked, indeterminate, disabled, onChange, title }) => (
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      onChange={onChange}
      onClick={(event) => event.stopPropagation()}
      title={title}
    />
  );

  if (loading && (!rows || rows.length === 0)) {
    return <EmptyState height={200}>Loading…</EmptyState>;
  }

  if (!loading && (!rows || rows.length === 0)) {
    return <EmptyState height={200} />;
  }

  const rangeStart = totalRows === 0 ? 0 : currentPage * rowsPerPage + 1;
  const rangeEnd = Math.min((currentPage + 1) * rowsPerPage, totalRows);
  const canPrev = currentPage > 0;
  const canNext = rangeEnd < totalRows;

  const renderPagination = () => (
    <div
      style={{
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "space-between" : "flex-end",
        flexWrap: "wrap",
        padding: isMobile ? "12px" : "12px 18px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(255,255,255,0.015)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginRight: isMobile ? 8 : 16, marginBottom: isMobile ? 10 : 0 }}>
        <span style={{ fontSize: 12, color: "#c3c8cc", marginRight: 8 }}>{isMobile ? "Rows:" : "Rows per page:"}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 6,
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              {rowsPerPage}
              <ChevronDown size={12} color="#c3c8cc" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" style={{ minWidth: 64 }}>
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <DropdownMenuItem key={n} onSelect={() => onRowsPerPageChange?.(n)}>
                {n}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <span style={{ fontSize: 12, color: "#c3c8cc", marginRight: isMobile ? 8 : 16, marginBottom: isMobile ? 10 : 0, alignSelf: "center" }}>
        {rangeStart}-{rangeEnd} of {totalRows}
      </span>
      <div style={{ display: "flex", marginLeft: isMobile ? "auto" : 0 }}>
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => onPageChange?.(currentPage - 1)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: isMobile ? 30 : 28,
            height: isMobile ? 30 : 28,
            marginRight: 4,
            borderRadius: 7,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.08)",
            color: canPrev ? "#fff" : "rgba(255,255,255,0.25)",
            cursor: canPrev ? "pointer" : "not-allowed",
          }}
        >
          <ChevronLeft size={15} />
        </button>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => onPageChange?.(currentPage + 1)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: isMobile ? 30 : 28,
            height: isMobile ? 30 : 28,
            borderRadius: 7,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.08)",
            color: canNext ? "#fff" : "rgba(255,255,255,0.25)",
            cursor: canNext ? "pointer" : "not-allowed",
          }}
        >
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    const selectCol = columns.find((c) => c.key === "select");
    const statusCol = mobileCompact ? columns.find((c) => c.key === "status") : undefined;
    const actionCol = mobileCompact
      ? columns.find((c) => c.key === "action" || c.key === "actions")
      : undefined;
    const primaryCol = columns.find((c) => c.key !== "select" && c !== statusCol && c !== actionCol) || columns[0];
    const bodyColumns = columns.filter((c) => c !== selectCol && c !== primaryCol && c !== statusCol && c !== actionCol);

    return (
      <div
        style={{
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.06)",
          overflow: "hidden",
          boxShadow: "0 16px 40px -26px rgba(0,0,0,0.6)",
          background: "linear-gradient(180deg, rgba(255,255,255,0.015), transparent)",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", padding: 10 }}>
          {(isSelectable || selectCol) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px 10px 12px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                marginBottom: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                {isSelectable ? (
                  renderCheckbox({
                    checked: areAllRowsGloballySelected,
                    indeterminate: selectedRows.length > 0 && !areAllRowsGloballySelected,
                    disabled: selectableRows.length === 0,
                    title: areAllRowsGloballySelected ? "Deselect all rows" : `Select all ${selectableRows.length} matching rows`,
                    onChange: handleSelectAllRows,
                  })
                ) : (
                  selectCol?.label
                )}
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "#8b9296" }}>
                  Select All
                </span>
              </div>
              <span style={{ fontSize: 11, color: "#666", fontFamily: "ui-monospace, monospace" }}>
                {rows.length} {rows.length === 1 ? "record" : "records"}
              </span>
            </div>
          )}

          {rows.map((row, index) => {
            const isOpen = openRowIndex === index;
            const primaryValue = primaryCol?.render ? primaryCol.render(row) : row[primaryCol?.key];
            const rowStyle = getRowStyle ? getRowStyle(row) : {};
            return (
              <div
                key={row.id ?? index}
                style={{
                  border: isOpen ? "1px solid rgba(0,173,181,0.22)" : "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 14,
                  background: isOpen
                    ? "linear-gradient(180deg, rgba(0,173,181,0.06), rgba(255,255,255,0.018))"
                    : "rgba(255,255,255,0.018)",
                  overflow: "hidden",
                  marginTop: index === 0 ? 0 : 10,
                  boxShadow: isOpen ? "0 14px 32px -26px rgba(0,173,181,0.6)" : "none",
                  ...rowStyle,
                }}
              >
                <button
                  type="button"
                  onClick={expandable ? () => setOpenRowIndex(isOpen ? null : index) : undefined}
                  disabled={!expandable}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "11px 12px",
                    border: "none",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    background: expandable ? "rgba(255,255,255,0.03)" : "transparent",
                    color: "#fff",
                    cursor: expandable ? "pointer" : "default",
                    textAlign: "left",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                    {isSelectable && (
                      <span onClick={(event) => event.stopPropagation()} style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
                        {renderCheckbox({
                          checked: isRowSelected(row),
                          disabled: !isRowSelectable(row) || (getRowCheckboxDisabled ? getRowCheckboxDisabled(row) : false),
                          title: isRowSelected(row) ? "Deselect row" : "Select row",
                          onChange: (event) => handleSelectRow(event, row),
                        })}
                      </span>
                    )}
                    {!isSelectable && selectCol && (
                      <span onClick={(event) => event.stopPropagation()} style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
                        {selectCol.render ? selectCol.render(row) : null}
                      </span>
                    )}
                    <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.08em", color: "#8b9296", textTransform: "uppercase" }}>
                        {primaryCol?.label || "Record"}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {primaryValue}
                      </span>
                    </span>
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 6 }}>
                    {statusCol && (
                      <span onClick={(event) => event.stopPropagation()}>
                        {statusCol.render ? statusCol.render(row) : row[statusCol.key]}
                      </span>
                    )}
                    {expandable && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          border: "1px solid rgba(255,255,255,0.08)",
                          background: isOpen ? "rgba(0,173,181,0.12)" : "rgba(255,255,255,0.025)",
                          flexShrink: 0,
                        }}
                      >
                        {isOpen ? <ChevronUp size={15} color="#c3c8cc" /> : <ChevronDown size={15} color="#c3c8cc" />}
                      </span>
                    )}
                  </div>
                </button>

                <div style={{ padding: "8px 12px" }}>
                  {bodyColumns.map((col, cIdx) => (
                    <div
                      key={col.key || cIdx}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(92px, 36%) minmax(0, 1fr)",
                        gap: 10,
                        padding: "7px 0",
                        borderBottom: cIdx === bodyColumns.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.08em", color: "#8b9296", textTransform: "uppercase" }}>
                        {col.label}
                      </div>
                      <div
                        style={{
                          minWidth: 0,
                          fontSize: 12.5,
                          color: "#c3c8cc",
                          textAlign: "right",
                          overflowWrap: "anywhere",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        {col.render ? col.render(row) : row[col.key]}
                      </div>
                    </div>
                  ))}

                  {expandable && isOpen && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: 11,
                        borderRadius: 12,
                        background: "rgba(0,0,0,0.2)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      {nestedTableHeader && (
                        <div style={{ fontSize: 12.5, fontWeight: 800, color: "#fff", marginBottom: 8 }}>
                          {nestedTableHeader}
                        </div>
                      )}
                      {showCustomNestedComponent ? (
                        customNestedComponent(row)
                      ) : row[nestedRowKey] && row[nestedRowKey].length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          {row[nestedRowKey].map((subRow, subIndex) => (
                            <div
                              key={subIndex}
                              style={{
                                padding: 10,
                                borderRadius: 10,
                                border: "1px solid rgba(255,255,255,0.06)",
                                background: "rgba(255,255,255,0.018)",
                                marginTop: subIndex === 0 ? 0 : 8,
                              }}
                            >
                              {nestedColumns.map((col) => (
                                <div
                                  key={col.key}
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "92px minmax(0, 1fr)",
                                    gap: 8,
                                    padding: "5px 0",
                                    borderTop: col === nestedColumns[0] ? "none" : "1px solid rgba(255,255,255,0.035)",
                                  }}
                                >
                                  <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.08em", color: "#8b9296", textTransform: "uppercase" }}>
                                    {col.label}
                                  </span>
                                  <div style={{ fontSize: 12.5, color: "#c3c8cc", overflowWrap: "anywhere", display: "flex", alignItems: "center", justifyContent: "flex-end", textAlign: "right" }}>
                                    {col.render ? col.render(subRow) : subRow[col.key]}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState height={80} />
                      )}
                    </div>
                  )}
                </div>

                {actionCol && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      padding: "8px 12px",
                      borderTop: "1px solid rgba(255,255,255,0.05)",
                      background: "rgba(255,255,255,0.015)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {actionCol.render ? actionCol.render(row) : row[actionCol.key]}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {renderPagination()}
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.06)",
        overflow: "hidden",
        boxShadow: "0 16px 40px -26px rgba(0,0,0,0.6)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.015), transparent)",
        width: "100%",
      }}
    >
      <div style={{ overflowX: "auto", maxWidth: "100%" }}>
        <table style={{ borderCollapse: "collapse", minWidth, width: "100%" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              {expandable && <th style={{ width: 32 }} />}
              {isSelectable && (
                <th style={{ width: 38, textAlign: "center", padding: "12px 8px" }}>
                  {renderCheckbox({
                    checked: areAllRowsGloballySelected,
                    indeterminate: selectedRows.length > 0 && !areAllRowsGloballySelected,
                    disabled: selectableRows.length === 0,
                    title: areAllRowsGloballySelected ? "Deselect all rows" : `Select all ${selectableRows.length} matching rows`,
                    onChange: handleSelectAllRows,
                  })}
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.align || "left",
                    padding: "12px 12px",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    color: "#c3c8cc",
                    whiteSpace: "nowrap",
                    width: col.width,
                    textTransform: typeof col.label === "string" ? "uppercase" : "none",
                  }}
                >
                  {typeof col.label === "string" ? col.label.toUpperCase() : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isOpen = openRowIndex === index;
              return (
                <React.Fragment key={row.id ?? index}>
                  <tr
                    onClick={expandable ? () => setOpenRowIndex(isOpen ? null : index) : undefined}
                    style={{
                      borderBottom: isOpen ? "none" : "1px solid rgba(255,255,255,0.04)",
                      cursor: expandable ? "pointer" : "default",
                      transition: "background 0.15s",
                      ...(getRowStyle ? getRowStyle(row) : {}),
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.02)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    {expandable && (
                      <td style={{ padding: "16px 10px", textAlign: "center", verticalAlign: "middle" }}>
                        {isOpen ? (
                          <ChevronUp size={15} color="#c3c8cc" />
                        ) : (
                          <ChevronDown size={15} color="#c3c8cc" />
                        )}
                      </td>
                    )}
                    {isSelectable && (
                      <td style={{ padding: "11px 8px", textAlign: "center", verticalAlign: "middle" }}>
                        {renderCheckbox({
                          checked: isRowSelected(row),
                          disabled: !isRowSelectable(row) || (getRowCheckboxDisabled ? getRowCheckboxDisabled(row) : false),
                          title: isRowSelected(row) ? "Deselect row" : "Select row",
                          onChange: (event) => handleSelectRow(event, row),
                        })}
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          padding: "11px 12px",
                          fontSize: 12.5,
                          color: "#c3c8cc",
                          textAlign: col.align || "left",
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                          maxWidth: col.maxWidth,
                          overflow: col.maxWidth ? "hidden" : undefined,
                          textOverflow: col.maxWidth ? "ellipsis" : undefined,
                        }}
                      >
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                  {expandable && isOpen && (
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <td colSpan={columns.length + (expandable ? 1 : 0) + (isSelectable ? 1 : 0)} style={{ padding: "0 16px 18px" }}>
                        {nestedTableHeader && (
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: "12px 0 8px" }}>
                            {nestedTableHeader}
                          </div>
                        )}
                        {showCustomNestedComponent ? (
                          customNestedComponent(row)
                        ) : row[nestedRowKey] && row[nestedRowKey].length > 0 ? (
                          <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                              <thead>
                                <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                                  {nestedColumns.map((col) => (
                                    <th
                                      key={col.key}
                                      style={{
                                        textAlign: col.align || "left",
                                        padding: "8px 12px",
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: "0.06em",
                                        color: "#c3c8cc",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {col.label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {row[nestedRowKey].map((subRow, subIndex) => (
                                  <tr key={subIndex} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                                    {nestedColumns.map((col) => (
                                      <td
                                        key={col.key}
                                        style={{
                                          padding: "8px 12px",
                                          fontSize: 12.5,
                                          color: "#c3c8cc",
                                          textAlign: col.align || "left",
                                        }}
                                      >
                                        {col.render ? col.render(subRow) : subRow[col.key]}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <EmptyState height={80} />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
}
